import { Box } from "@mui/material";
import React, { useState } from "react";
import { Draggable, DraggableRubric, Droppable } from "react-beautiful-dnd";
import { Base } from "ui/Scripts/source/framework/base";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { TaskDataRowDataType } from "ui/Scripts/source/models/enums";
import { CopyToolButton } from "source/components/framework/Button/CopyToolButton";
import { TaskDynamicFormCol } from "source/components/task/TaskDynamicForm/TaskDynamicFormCol";
import { PopupMenu } from "source/components/framework/PopupMenu";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";

export interface FormDesignerDataRow {
    id: string;
    locked?: boolean;
    width?: number;
    rows: ITaskTypeCol[];
}

interface IFormDesignerBuilderFormBuilderProps {
    droppableId: string;
    pickerDroppableId: string;
    designerRows?: FormDesignerDataRow[];
    taskDataRow?: ITaskDataRow;
    taskTypeCol: ITaskTypeCol;
    taskTypeCols: ITaskTypeCol[];
    draggingWidth?: number;
    draggingSource?: string;
    onClick?: (id: string) => void;
    onRemoveComponent: (id: string) => void;
    onCopyComponent: (id: string) => void;
}

const getRenderItem = (items: ITaskTypeCol[],
    fieldName: string,
    calculatedFieldSelected: boolean,
    calculatedFieldExpressionFields: string[],
    taskDataRows: ITaskDataRowCol[],
    isFirst: boolean,
    isLast: boolean,
    onClick: (id: string) => void,
    onRemove: (id: string) => void,
    onCopy: (id: string) => void) =>
    // eslint-disable-next-line react/display-name
    (provided, _snapshot, rubric: DraggableRubric) => {
        const item = items[rubric.source.index];
        const showFieldName = calculatedFieldSelected && (
            item.dataType === TaskDataRowDataType.Integer ||
            item.dataType === TaskDataRowDataType.Number ||
            item.dataType === TaskDataRowDataType.Date ||
            item.dataType === TaskDataRowDataType.Time ||
            item.dataType === TaskDataRowDataType.TimeSpan ||
            item.dataType === TaskDataRowDataType.String ||
            item.dataType === TaskDataRowDataType.StringArray ||
            item.dataType === TaskDataRowDataType.Boolean) && (item.field !== fieldName);
        const showFieldNameColor = showFieldName && calculatedFieldExpressionFields.indexOf(item.field) > -1 ? "success.dark" : "primary.main";
        return (
            <div
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                ref={provided.innerRef}
                className={"col " + (!Base.isNullOrEmpty(item.class) ? item.class : "") + (isFirst ? " firstCol" : "") + (isLast ? " lastCol" : "")}
            >
                <Box
                    sx={{
                        border: "4px dashed",
                        borderColor: fieldName === item.field ? "text.secondary" : "background.paper",
                        margin: "0 -15px",
                        padding: "0 15px",
                    }}
                    onClick={(e) => {
                        onClick(item.field);
                    }}
                >
                    <TaskDynamicFormCol
                        isReadOnly={false}
                        colIsReadOnly={item.readOnly}
                        showFieldName={showFieldName}
                        showFieldNameColor={showFieldNameColor}
                        taskTypeCol={item}
                        taskDataRowCol={taskDataRows?.find(
                            (i) => i.field === item.field
                        )}
                        ignoreClass
                        onChange={() => { }}
                    />
                    {fieldName === item.field &&
                        <PopupMenu>
                            <CopyToolButton
                                onClick={(_e) => onCopy(item.field)}
                            />
                            <RemoveToolButton
                                onClick={(_e) => onRemove(item.field)}
                            />
                        </PopupMenu>
                    }
                </Box>
            </div>
        );
    };

export const FormDesignerBuilderFormBuilder = (props: IFormDesignerBuilderFormBuilderProps) => {
    const [draggableLocked, setDraggableLocked] = useState(false);

    const handleMouseEnter = (containerId) => {
        if (props.draggingSource === containerId || props.designerRows.find((item) => item.id === containerId).width + props.draggingWidth <= 12) {
            setDraggableLocked(true);
        }
    };

    const handleMouseLeave = (_event) => {
        setDraggableLocked(false);
    };

    const dropAllowedFromDraggingSource = props.draggingSource?.startsWith(props.droppableId) || props.draggingSource === props.pickerDroppableId;

    const isCalculatedField = TaskTypeCol.isCalculatedField(props.taskTypeCol);

    const expressionFields = TaskDataRowCol.getExpressionsFieldNames(props.taskTypeCols, props.taskTypeCol?.expression);

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: "100px", marginLeft: "-16px", marginRight: "-16px", overflow: "auto" }}>
                <Droppable droppableId={props.droppableId} isDropDisabled={draggableLocked || !dropAllowedFromDraggingSource}>
                    {(provided, snapshot) => (
                        <Box
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                overflowY: "auto",
                                bgcolor: snapshot.isDraggingOver ? "divider" : "background.paper",
                                paddingBottom: "50px",
                            }}
                            className="jsonFormData designer"
                        >
                            {props.designerRows?.map((designerRow, rowIndex) => (
                                <Draggable
                                    key={designerRow.id}
                                    draggableId={designerRow.id}
                                    index={rowIndex}
                                    isDragDisabled={true}
                                >
                                    {(provided, _snapshot) => (
                                        <Box
                                            id={designerRow.id}
                                            key={designerRow.id}
                                            ref={provided.innerRef}
                                            onMouseEnter={() => handleMouseEnter(designerRow.id)}
                                            onMouseLeave={handleMouseLeave}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            sx={{
                                                left: "auto !important",
                                                top: "auto !important",
                                            }}
                                        >
                                            <Droppable
                                                droppableId={`${designerRow.id}`}
                                                key={designerRow.id}
                                                direction="horizontal"
                                                isDropDisabled={!dropAllowedFromDraggingSource || (((props.draggingWidth + designerRow.width) > 12) && props.draggingSource !== designerRow.id)}
                                                renderClone={getRenderItem(designerRow.rows, props.taskTypeCol?.field, isCalculatedField, expressionFields, props.taskDataRow?.cols, false, false, props.onClick, props.onRemoveComponent, props.onCopyComponent)}
                                                getContainerForClone={() => {
                                                    return document.getElementById("formDesignerDragItemContainer") as any;
                                                }}
                                            >
                                                {(provided, snapshot) => (
                                                    <Box
                                                        {...provided.droppableProps}
                                                        ref={provided.innerRef}
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            flexGrow: 0,
                                                            minWidth: "100%",
                                                            bgcolor: snapshot.isDraggingOver ? "divider" : "background.paper",
                                                        }}
                                                    >
                                                        {designerRow.rows.map((taskTypeCol, fieldIndex) => (
                                                            <Draggable
                                                                key={taskTypeCol.field}
                                                                draggableId={`${props.droppableId}${taskTypeCol.field}`}
                                                                index={fieldIndex}
                                                            >
                                                                {getRenderItem(designerRow.rows, props.taskTypeCol?.field, isCalculatedField, expressionFields, props.taskDataRow?.cols, fieldIndex === 0, fieldIndex === designerRow.rows.length - 1, props.onClick, props.onRemoveComponent, props.onCopyComponent)}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </Box>
                                                )}
                                            </Droppable>
                                        </Box>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </Box>
        </>
    );
};
